import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import {
	Calendar,
	CalendarCheck01,
	Hash01,
	Maximize02,
	Trash01,
} from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import z from "zod";
import { DeleteTicketModal } from "@/components/Organisms/DeleteTicketModal/DeleteTicketModal";
import { DestinyEntityDrawer } from "@/components/Organisms/DestinyEntityDrawer/DestinyEntityDrawer";
import { DetailsState } from "@/components/Organisms/Details/Details";
import { DrawerProvider } from "@/contexts/drawer-context";
import {
	TicketDetailsTab,
	TicketEntityDetails,
} from "@/features/tickets/components";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDestinyDrawerCurrentTab } from "@/lib/use-destiny-drawer-current-tab";
import { authQueryOptions } from "@/services/auth";
import { ticketQueryOptions } from "@/services/ticket";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

export const TabSchema = z.enum(["details", "activity"]);

export const SearchSchema = z.object({
	/** @deprecated Autosave Details panel — kept for legacy links that pass mode. */
	mode: z.enum(["edit", "view"]).optional(),
	tab: TabSchema.optional(),
	organizationId: z.string().optional(),
	ticketId: z.string().optional(),
	delete: z.boolean().optional(),
});

export type Tab = z.infer<typeof TabSchema>;
export type Search = z.infer<typeof SearchSchema>;

export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/tickets/(ticket)/$ticketId/_drawer",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function RouteComponent() {
	return (
		<DrawerProvider>
			<TicketDrawer />
		</DrawerProvider>
	);
}

/**
 * Ticket detail drawer — Figma composition: DrawerHeader (type=ticket) +
 * Details (entity=ticket) via {@link TicketEntityDetails}.
 */
function TicketDrawer() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/tickets/(ticket)/$ticketId/_drawer",
	});
	const search = useSearch({
		from: "/_authenticated/(web-app)/_web/web/$orgId/tickets/(ticket)/$ticketId/_drawer",
	});
	const [isOpen, setIsOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const currentTab = useDestinyDrawerCurrentTab(location.pathname);

	const ticketById = useQuery(
		ticketQueryOptions.detail(params.ticketId, {
			organizationId: params.orgId,
		}),
	);

	const canDeleteTickets = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteTicket",
			entityType: "Ticket",
			entityId: params.ticketId,
		}),
		enabled: !!params.ticketId,
	});

	useEffect(() => {
		setIsOpen(true);
	}, []);

	const drawerMetadata = useMemo(() => {
		const ticket = ticketById.data;
		if (!ticket) return [];

		const items: {
			id: string;
			label: string;
			icon: typeof Hash01;
		}[] = [];

		if (ticket.ticketNumber != null) {
			items.push({
				id: "ticket-number",
				label: String(ticket.ticketNumber),
				icon: Hash01,
			});
		}
		if (ticket.createdAt) {
			items.push({
				id: "created-at",
				label: createdMetadataLabel(t, ticket.createdAt),
				icon: Calendar,
			});
		}
		if (ticket.updatedAt) {
			items.push({
				id: "updated-at",
				label: updatedMetadataLabel(t, ticket.updatedAt),
				icon: CalendarCheck01,
			});
		}
		return items;
	}, [ticketById.data, t]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			navigate({
				to: `/web/$orgId/tickets`,
				params: { orgId: params.orgId },
				replace: true,
			});
		}, 200);
	};

	const handleDelete = () => {
		navigate({
			to: location.pathname,
			search: (prev) => ({ ...prev, delete: true }),
		});
		setIsDeleteModalOpen(true);
	};

	const handleOpenWorkspace = () => {
		navigate({
			to: "/web/$orgId/tickets/$ticketId/workspace",
			params: { orgId: params.orgId, ticketId: params.ticketId },
		});
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			handleClose();
		}
	};

	const title = ticketById.data?.name
		? ticketById.data?.name
		: t("main.tickets.drawer.title");

	const detailsTabs = [
		{
			label: t("main.tickets.drawer.tabs.details.label"),
			id: TicketDetailsTab.Details,
		},
		{
			label: t("main.tickets.drawer.tabs.activity.label"),
			id: TicketDetailsTab.Activity,
		},
	];

	const handleTabChange = (tabId: string) => {
		if (tabId === currentTab) return;
		navigate({
			to: `/web/$orgId/tickets/$ticketId/${tabId}`,
			params: {
				orgId: params.orgId,
				ticketId: params.ticketId,
			},
			search: (prev) => prev,
		});
	};

	const clearDeleteSearch = () => {
		navigate({
			to: location.pathname,
			search: (prev) => {
				const { delete: _, ...rest } = prev;
				return rest;
			},
		});
		setIsDeleteModalOpen(false);
	};

	return (
		<DestinyEntityDrawer
			type="ticket"
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			title={title}
			metadata={drawerMetadata}
			isError={ticketById.isError}
			tabs={detailsTabs}
			currentTab={currentTab}
			onTabChange={handleTabChange}
			actions={[
				{
					label: t("main.tickets.workspace.action"),
					icon: Maximize02,
					color: "secondary",
					onPress: handleOpenWorkspace,
				},
			]}
			overflowActions={
				canDeleteTickets.data
					? [
							{
								label: t("main.tickets.drawer.deleteTicket.button"),
								icon: Trash01,
								onPress: handleDelete,
							},
						]
					: []
			}
			details={
				<TicketEntityDetails
					orgId={params.orgId}
					ticketId={params.ticketId}
					state={
						ticketById.isPending ? DetailsState.Loading : DetailsState.Default
					}
					selectedTabId={currentTab}
					onTabChange={handleTabChange}
					tabs={detailsTabs}
					className="max-md:[&_[role=tablist]]:hidden"
				/>
			}
			outlet={<Outlet />}
		>
			{({ close }) => (
				<DeleteTicketModal
					isOpen={isDeleteModalOpen || !!search.delete}
					setIsOpen={(open) => {
						if (!open) clearDeleteSearch();
					}}
					handleClose={clearDeleteSearch}
					onDeleteSuccess={() => {
						close();
						handleClose();
					}}
				/>
			)}
		</DestinyEntityDrawer>
	);
}
